import { MapPin } from "lucide-react";
import { Dispatch, SetStateAction, useEffect, useRef } from "react";
import { MapRef, Marker } from "react-map-gl";

import { useFormLogic } from "./cards/custom-hooks";
import { Basemap } from "components/basemap/basemap";
import { useCustomSnackbars } from "components/snackbars/useCustomSnackbars";
import { requestTransformer } from "pages/geoscape-data/explorer/explorer-page";
import { ActionCard } from "./cards/action-card";
import { FeatureCardComponent } from "./cards/feature-card";
import { Legend } from "./cards/Legend";
import { useFloodDemoState } from "./custom-hooks/useFloodDemoState";
import { useMapControls } from "./custom-hooks/useMapControls";
import { MapLayer, stepsDemos } from "./step-config-file/stepConfigFile";
import { GeoJsonDemoMapLayer, Mode } from "./step-config-file/types";

export const FloodDataPage = () => {
  const FREE_EXPLORE_STEP: number = 11;

  const initialZoom: number = 4;

  const {
    demoStep,
    attributes,
    dynamicZoom,
    markerPosition,
    markerShow,
    selectedLayer,
    setDemoStep,
    setAttributes,
    setDynamicZoom,
    setMarkerPosition,
    setShowMarker,
    setSelectedLayer,
  } = useFloodDemoState();

  const currentStepDemo = stepsDemos[demoStep];
  const previousStepDemo = stepsDemos[demoStep - 1] ?? stepsDemos[1];
  const nextStepDemo = stepsDemos[demoStep + 1] ?? stepsDemos[1];
  const CurrentComponentStep = currentStepDemo.component;
  const currentMapConfigStep = currentStepDemo.mapConfig;

  const mapRef = useRef<MapRef>(null);

  const nextStep = (setDemoStep: Dispatch<SetStateAction<number>>) => {
    setDemoStep((prevStep) => {
      return prevStep + 1;
    });
  };

  const backStep = (setDemoStep: Dispatch<SetStateAction<number>>) => {
    setDemoStep((prevStep) => {
      return prevStep - 1;
    });
  };

  const goToStep = (setDemoStep: Dispatch<SetStateAction<number>>, stepNumber: number) => {
    setDemoStep(stepNumber);
  };

  const isInteractiveStep = currentStepDemo.id === FREE_EXPLORE_STEP;

  // useMapControls -> Has the logic for Fly To, Map Interaction Controls
  const {
    boundingBox,
    minZoom,
    coordinates,
    handleMarker,
    onClick,
    moveToNextLocation,
    highlightedLayer,
    selectedId,
  } = useMapControls({
    mapRef,
    setAttributes,
    setSelectedLayer,
    setMarkerPosition,
    setShowMarker,
    setDynamicZoom,
    isInteractiveStep,
    initialCoordinates: currentStepDemo.coordinates,
    currentStep: demoStep,
  });

  const { formState, handleFormChange } = useFormLogic({
    // people: 1,
    // season: Season.Summer,
    mode: Mode.ffe,
  });

  const { enqueueQueryFailed } = useCustomSnackbars();

  // Auto-switch modes based on step
  useEffect(() => {
    switch (demoStep) {
      case 1:
      case 2:
      case 3:
        handleFormChange({ mode: Mode.ffe });
        break;
      case 4:
      case 5:
        handleFormChange({ mode: Mode.real });
        break;
      case 6:
        handleFormChange({ mode: Mode.model_5 });
        break;
      case 7:
        handleFormChange({ mode: Mode.model_500 });
        break;
      case 8:
      case 9:
      case 10:
        handleFormChange({ mode: Mode.model_1500 });
        break;
      case 11:
        handleFormChange({ mode: Mode.real });
        break;
    }
  }, [demoStep]);

  // Auto-select feature in step 9
  useEffect(() => {
    if (demoStep === 9 && mapRef.current) {
      const targetLng = 146.16289718499246;
      const targetLat = -18.65506208479556;

      // Set marker position
      setMarkerPosition({
        longitude: targetLng,
        latitude: targetLat,
      });
      setShowMarker(true);

      // Query features at the location after layers are rendered
      setTimeout(() => {
        const map = mapRef.current?.getMap();
        if (map) {
          const point = map.project([targetLng, targetLat]);
          const features = map.queryRenderedFeatures(point);

          if (features && features.length > 0) {
            // Find building feature first
            const buildingFeature = features.find(
              (x) => x.layer.id === "flood-building-height"
            );

            if (buildingFeature) {
              setSelectedLayer("flood-building-height");
              setAttributes(buildingFeature as any);
            } else {
              // Fallback to cadastre
              const cadastreFeature = features.find(
                (x) => x.layer.id === "flood-cadastre"
              );
              if (cadastreFeature) {
                setSelectedLayer("flood-cadastre");
                setAttributes(cadastreFeature as any);
              }
            }
          }
        }
      }, 500); // Wait for map to render layers
    }
  }, [demoStep, mapRef]);

  return (
    // Here we define the layers. Layers that go on the top of the map and containt the GeoJson information - Geometry, Properties, etc.
    <>
      <Basemap
        mapRef={mapRef}
        attributionControl={true}
        latitude={coordinates.latitude}
        longitude={coordinates.longitude}
        zoom={initialZoom}
        onZoom={(zoom) => setDynamicZoom(zoom)}
        onClick={isInteractiveStep ? onClick : undefined}
        style={{
          width: "100%",
          height: "100%",
          position: "absolute",
          left: 0,
          top: 0,
        }}
        minZoom={minZoom}
        // maxZoom={maxZoom}
        boundingBox={boundingBox}
        transformRequest={requestTransformer}
      >
        {/* Display the Card Component defined in the Steps */}
        <CurrentComponentStep
          moveToNextLocation={moveToNextLocation}
          nextStep={() => nextStep(setDemoStep)}
          backStep={() => backStep(setDemoStep)}
          goToStep={(stepNumber: number) => goToStep(setDemoStep, stepNumber)}
          previousCoordinates={previousStepDemo.coordinates}
          previousZoomLevel={previousStepDemo.mapConfig.zoomLevel}
          nextCoordinates={nextStepDemo.coordinates}
          zoomLevel={nextStepDemo.mapConfig.zoomLevel}
          attributes={attributes}
          selectedLayer={selectedLayer}
          handleMarker={() => handleMarker()}
          formState={formState}
          {...currentStepDemo}
        />

        {/* Show FeatureCardComponent in step 9 when a feature is selected */}
        {currentStepDemo.id === 9 && (
          <FeatureCardComponent
            moveToNextLocation={moveToNextLocation}
            nextStep={() => nextStep(setDemoStep)}
            backStep={() => backStep(setDemoStep)}
            goToStep={(stepNumber: number) => goToStep(setDemoStep, stepNumber)}
            previousCoordinates={previousStepDemo.coordinates}
            previousZoomLevel={previousStepDemo.mapConfig.zoomLevel}
            nextCoordinates={nextStepDemo.coordinates}
            zoomLevel={nextStepDemo.mapConfig.zoomLevel}
            attributes={attributes}
            selectedLayer={selectedLayer}
            handleMarker={() => handleMarker()}
            formState={formState}
            {...currentStepDemo}
          />
        )}

        {/* The Marker and the Action Card - shown in interactive steps and steps 8-11 (except step 10) */}
        {(currentStepDemo.id === FREE_EXPLORE_STEP ||
          (currentStepDemo.id >= 8 && currentStepDemo.id <= 11)) && (
          <>
            {/* Hide marker in step 10 */}
            {markerShow && currentStepDemo.id !== 10 && (
              <Marker
                key={"selected-address"}
                longitude={markerPosition.longitude}
                latitude={markerPosition.latitude}
                style={{
                  position: "absolute",
                  left: 0,
                  top: 0,
                }}
              >
                <MapPin className="size-8 text-destructive" fill="currentColor" />
              </Marker>
            )}

            {/* Show ActionCard in steps 8-9, 11 (hide in step 10) */}
            {currentStepDemo.id >= 8 && currentStepDemo.id !== 10 && (
              <div className="absolute bottom-10 left-4 flex flex-row items-end gap-2.5 pl-2.5">
                <ActionCard
                  formState={formState}
                  onFormChange={handleFormChange}
                />
                {/* Show Legend in steps 8, 9, and 11 (explore phase) */}
                {(currentStepDemo.id === 8 ||
                  currentStepDemo.id === 9 ||
                  currentStepDemo.id === 11) && <Legend />}
              </div>
            )}
          </>
        )}

        {/* Render GeoJSON layers for all steps that have them */}
        {currentStepDemo.geojsonLayers &&
          currentStepDemo.geojsonLayers.map(
            (layer: GeoJsonDemoMapLayer, index: number) => {
              return (
                <MapLayer
                  key={`${layer.layerName}-${index}`}
                  geojsonPath={layer.geojsonPath}
                  formState={formState}
                  layer_name={layer.layerName}
                  highlightedLayer={highlightedLayer}
                  style={layer.createStyle(formState, selectedId)}
                  beforeId={layer.beforeId}
                />
              );
            }
          )}
      </Basemap>
    </>
  );
};
